import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { VEHICLE_TYPES, CAN_MASTER } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Plus, Search, Pencil, Truck, ImagePlus } from "lucide-react";

const empty = { customer_id: "", plate: "", hull_number: "", vehicle_type: VEHICLE_TYPES[0], brand: "", model: "", year: "", color: "", chassis_number: "", engine_number: "", body_type: "", photo_path: "", notes: "", active: true };

export default function Vehicles() {
  const { user } = useAuth();
  const canEdit = user && CAN_MASTER.includes(user.role);
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/vehicles", { params: q ? { q } : {} }); setList(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); api.get("/customers", { params: { active: "true" } }).then((r) => setCustomers(r.data)); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (v) => { setForm({ ...empty, ...v }); setEditId(v.id); setOpen(true); };

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("folder", "vehicles");
      const { data } = await api.post("/upload", fd);
      set("photo_path", data.storage_path);
      toast.success("Foto diunggah");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async (force = false, overrideReason = "") => {
    if (!form.customer_id || !form.plate || !form.vehicle_type) { toast.error("Customer, No. Polisi, dan Jenis wajib diisi."); return; }
    setSaving(true);
    try {
      if (editId) { await api.put(`/vehicles/${editId}`, form); toast.success("Kendaraan diperbarui"); }
      else { await api.post("/vehicles", { ...form, force, override_reason: overrideReason }); toast.success("Kendaraan dibuat"); }
      setOpen(false); load();
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 409 && !editId) {
        if (user.role === "Owner") {
          const reason = window.prompt(detail + "\n\nMasukkan alasan override (wajib):");
          if (reason) { await save(true, reason); return; }
        } else { toast.error(detail); }
      } else { toast.error(detail); }
    } finally { setSaving(false); }
  };

  const toggle = async (v) => {
    try { await api.patch(`/vehicles/${v.id}/status`); toast.success("Status diperbarui"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="vehicles-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl font-bold text-navy">Master Kendaraan / Armada</h1>
          <p className="text-sm text-muted-foreground">{list.length} kendaraan</p></div>
        {canEdit && <Button onClick={openNew} data-testid="new-vehicle-btn" className="bg-primary hover:bg-red-700"><Plus size={16} className="mr-1.5" /> Tambah Kendaraan</Button>}
      </div>

      <Card className="p-3">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="vehicle-search" placeholder="Cari No. Polisi, No. Lambung, merek, model..." className="pl-9" />
        </form>
      </Card>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div> : (
        list.length === 0 ? <Card className="p-10 text-center text-muted-foreground"><Truck className="mx-auto mb-2 opacity-40" /> Belum ada kendaraan.</Card> : (
          <>
            <Card className="hidden md:block overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50"><tr className="text-left text-xs text-muted-foreground">
                  <th className="py-2.5 px-3">Vehicle ID</th><th className="px-3">No. Polisi</th><th className="px-3">Jenis</th><th className="px-3">Merek/Model</th><th className="px-3">Customer</th><th className="px-3">Status</th><th></th>
                </tr></thead>
                <tbody>
                  {list.map((v) => (
                    <tr key={v.id} data-testid={`vehicle-row-${v.vehicle_id}`} className="border-t hover:bg-slate-50">
                      <td className="py-2.5 px-3 font-mono-k3 text-xs">{v.vehicle_id}</td>
                      <td className="px-3 font-semibold">{v.plate}</td>
                      <td className="px-3 text-xs">{v.vehicle_type}</td>
                      <td className="px-3 text-xs">{v.brand} {v.model}</td>
                      <td className="px-3 text-xs">{v.customer_name}</td>
                      <td className="px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${v.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{v.active ? "Active" : "Inactive"}</span></td>
                      <td className="px-3">{canEdit && <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`edit-vehicle-${v.vehicle_id}`}><Pencil size={14} /></Button>
                        <Switch checked={v.active} onCheckedChange={() => toggle(v)} data-testid={`toggle-vehicle-${v.vehicle_id}`} />
                      </div>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <div className="md:hidden space-y-2">
              {list.map((v) => (
                <Card key={v.id} data-testid={`vehicle-card-${v.vehicle_id}`} className="p-3">
                  <div className="flex justify-between"><span className="font-mono-k3 text-xs">{v.vehicle_id}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${v.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{v.active ? "Active" : "Inactive"}</span></div>
                  <div className="font-semibold mt-1">{v.plate}</div>
                  <div className="text-xs text-muted-foreground">{v.vehicle_type} · {v.brand} {v.model} · {v.customer_name}</div>
                  {canEdit && <Button size="sm" variant="outline" className="mt-2" onClick={() => openEdit(v)}>Edit</Button>}
                </Card>
              ))}
            </div>
          </>
        )
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Kendaraan" : "Tambah Kendaraan"}</DialogTitle>
            <DialogDescription>Isi data master kendaraan/armada.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Customer / Pemilik *</Label>
              <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
                <SelectTrigger data-testid="vehicle-customer"><SelectValue placeholder="Pilih customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Nomor Polisi *</Label><Input value={form.plate} onChange={(e) => set("plate", e.target.value.toUpperCase())} data-testid="vehicle-plate" placeholder="B 9894 PEJ" /></div>
              <div className="space-y-1"><Label>No. Lambung</Label><Input value={form.hull_number} onChange={(e) => set("hull_number", e.target.value)} data-testid="vehicle-hull" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Jenis Kendaraan *</Label>
                <Select value={form.vehicle_type} onValueChange={(v) => set("vehicle_type", v)}>
                  <SelectTrigger data-testid="vehicle-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label>Jenis Body/Karoseri</Label><Input value={form.body_type} onChange={(e) => set("body_type", e.target.value)} data-testid="vehicle-body" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Merek</Label><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="vehicle-brand" /></div>
              <div className="space-y-1"><Label>Model/Tipe</Label><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="vehicle-model" /></div>
              <div className="space-y-1"><Label>Tahun</Label><Input value={form.year} onChange={(e) => set("year", e.target.value)} data-testid="vehicle-year" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Warna</Label><Input value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="vehicle-color" /></div>
              <div className="space-y-1"><Label>No. Rangka</Label><Input value={form.chassis_number} onChange={(e) => set("chassis_number", e.target.value)} data-testid="vehicle-chassis" /></div>
              <div className="space-y-1"><Label>No. Mesin</Label><Input value={form.engine_number} onChange={(e) => set("engine_number", e.target.value)} data-testid="vehicle-engine" /></div>
            </div>
            <div className="space-y-1"><Label>Foto Kendaraan</Label>
              <div className="flex items-center gap-3">
                {form.photo_path && <img src={`${API}/files/${form.photo_path}`} alt="" className="h-16 w-16 object-cover rounded-md border" />}
                <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-md text-sm cursor-pointer hover:bg-slate-50" data-testid="vehicle-photo-label">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Unggah Foto
                  <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="vehicle-photo-input" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
                </label>
              </div>
            </div>
            <div className="space-y-1"><Label>Catatan</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="vehicle-notes" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="vehicle-active" /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={() => save(false)} disabled={saving} data-testid="save-vehicle-btn" className="bg-primary hover:bg-red-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
